import React, { useEffect, useRef, useState } from 'react';
import './momentum-upload-page.styles.scss';
import { useSelector } from 'react-redux';
import { usePapaParse } from 'react-papaparse';
import { membershipRestrictions } from '@/utils/membershipRestrictions';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import { useLocation, useNavigate } from 'react-router-dom';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import UploadItemPreview from './upload-momentum-preview.component';
import { RandomString } from '@/utils/randomString';
import { remove } from 'firebase/database';
import { UseCloudFunction } from '@/utils/firebase.utils';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import useAnalytics from '@/utils/useAnalytics';
import UploadMomentumPreview from './upload-momentum-preview.component';
import { useEffectiveMembership } from '@/utils/useEffectiveMembership';

const MomentumUploadPage = ({
  gameData,
  gameElements,
  role
}) => {

  const userProfile = useSelector(state => state.userData.userProfile);
  const membership = useEffectiveMembership();
  const userMembership = useSelector(state => state.userData.membership);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [arrToUpload, setArrToUpload] = useState([]);
  const [availableSpots, setAvailableSpots] = useState(0);
  const [loading, setLoading] = useState(false);
  const { trackEvent, trackException } = useAnalytics();
  const fileReader = new FileReader();
  const { readString } = usePapaParse();
  const approveRef = useRef();
  const accessDeniedRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!role || !gameData?.path) return;
    if (role !== 'manager' && role !== 'owner') {
      setAlertMessage('You do not have permission to upload momentum cards.');
      accessDeniedRef.current = () => navigate(`/manage/${gameData.path}/elements/momentum`);
    }
  }, [role, gameData?.path, navigate])

  useEffect(() => {
    if (!membership || !membershipRestrictions || !gameData) return;
    const num = membershipRestrictions[membership.membership].elementCount;
    if (num === Infinity) {
      setAvailableSpots('Unlimited');
      return;
    }
    console.log(num);
    console.log(membership);
    setAvailableSpots(num - (gameData.createdElementTotal ?? 0));
  }, [membership, membershipRestrictions, gameData])

  function clickUpload() {
    document.getElementById('fileUpload').click();
  }

  async function handleUpload(file) {
    document.getElementById('fileUpload').value = null;
    const ext = file.name.match(/\.([^\.]+)$/)[1].toLowerCase();
    console.log(ext);
    const allowed = ['csv']
    if (!allowed.includes(ext)) {
      setAlertMessage('That file type is not allowed to be uploaded. Try a different file.');
      setArrToUpload([]);
      document.getElementById('fileUpload').value = null;
      return;
    }
    const csvOutput = await readFileAsync(file)
    // console.log(csvOutput)
    const arr = await getParsedData(csvOutput)
    console.log(arr);
    const objArr = createObjs(arr)
    console.log(objArr);
    setArrToUpload(objArr);
  }

  function readFileAsync(file) {
    return new Promise((resolve, reject) => {
      fileReader.onload = (event) => {
        resolve(event.target.result);
      };
      fileReader.readAsText(file);
    })
  }

  function getParsedData(arr) {
    return new Promise((resolve, reject) => {
      readString(arr, {
        worker: true,
        complete: (results) => {
          // console.log('---------------------------');
          // console.log(results.data);
          // console.log('---------------------------');
          resolve(results.data)
        },
      });
    })
  }

  function createObjs(arr) {
    const template = [
      "Name",
      "Description",
      "Momentum Direction",
      "Category",
      "Currency Earned",
      "Item(s) Earned",
      "Prize Pack(s) Earned",
      "Currency Lost",
      "Item(s) Lost",
      "Available Date",
      "Unavailable Date"
    ]
    const headers = arr[0]
    console.log(template);
    console.log(headers);
    let validCSV = true;
    for (let i = 0; i < template.length; i++) {
      if (template[i] !== headers[i]) {
        validCSV = false;
        console.log(template[i], headers[i]);
        break;
      }
    }
    if (!validCSV) {
      setAlertMessage('Please use the correct template when uploading a list of Momentum Cards.');
      return false;
    }
    let objArr = [];
    let startingPoint = 1;
    if (arr[1][0].includes('required')) {
      startingPoint = 2;
    }
    const keyList = [
      "name",  // str
      "desc",  // str
      "opt_momentumDirection",  // str
      "opt_cat",  // str
      "opt_earnedCurrency",  // number
      "opt_earnedItems",  // IDs
      "opt_earnedPrizePacks",  // IDs
      "opt_removeCurrency",  // number
      "opt_removeItems",  // IDs
      "opt_dateOpen",  // date
      "opt_dateClose" // date
    ]
    const now = new Date().getTime();
    const gameCode = gameData.gameId.substring(gameData.gameId.indexOf('-') - 5, gameData.gameId.indexOf('-') + 6);
    let len = 0
    if (availableSpots === 'Unlimited') {
      len = arr.length <= 200 ? arr.length : 200;
    } else {
      len = arr.length - startingPoint <= availableSpots ? arr.length : availableSpots;
    }

    console.log(startingPoint, len);
    for (let a = startingPoint; a < len; a++) {
      const row = arr[a];

      // skip row if item already exists with same name
      if (Object.values(gameElements)
        .filter(e =>
          e.name.toLowerCase() === row[0].trim().toLowerCase()
        ).length > 0
      ) {
        continue;
      }

      let obj = {};
      console.log(row);

      // set momentum direction as lower case
      let direction = row[2].trim().toLowerCase();

      if (direction === 'negative') {
        row[4] = null;
        row[5] = null;
        row[6] = null;
      }
      else if (direction === 'positive') {
        row[7] = null;
        row[8] = null;
      }
      else if (direction === 'neutral') {
        row[4] = null;
        row[5] = null;
        row[6] = null;
        row[7] = null;
        row[8] = null;
      }

      for (let r = 0; r < row.length; r++) {

        // checks to make sure it's a number
        let val = row[r];
        if (r === 2) {
          obj[keyList[r]] = direction;
        }
        else if (row[r] && (
          r === 4 ||
          r === 7
        )) {
          val = Number(row[r].trim());
          // check if value is a number, if not assign null
          if (isNaN(val)) {
            obj[keyList[r]] = null;
          } else {
            obj[keyList[r]] = val;
          }
        }

        // create arrays from comma separated strings
        else if (row[r] && (
          r === 5 ||
          r === 6 ||
          r === 8
        )) {
          const val = row[r].replace(/\s/g, '').split(',');

          // go through item list to get id's instead of paths
          // 6, 7 and 8 are prize packs and earned items which require a different format in the obj
          let newObj = {};
          for (let v of val) {
            const el = Object.values(gameElements).find(e => e.path === v);
            if (
              !el || 
              (
                el.type === 'item' &&
                el.opt_itemType !== 'inventory' && 
                el.opt_itemType !== 'collectible'
              )
            ) {
              continue;
            }
            
            newObj[el.id] = {
              'id': el.id,
              'quantity': 1
            }
          }
          obj[keyList[r]] = newObj;
        }
        
        // create booleans
        else if (r === 9) {
          obj[keyList[r]] = row[r].trim().toLowerCase() === 'true' ? true : false;
        }

        // create date objects
        else if (row[r] && (r === 10 || r === 11)) {
          const date = new Date(row[r]);

          // 1. Check if the date object is valid
          // 2. Check if getTime() returns a number (not NaN)
          if (!isNaN(date.getTime())) {
            obj[keyList[r]] = date.getTime();
          } else {
            obj[keyList[r]] = null;
          }
        }
        
        // default
        else if (row[r]) {
          obj[keyList[r]] = row[r].trim();
        }
      }
      if (!obj.opt_dateOpen) {
        obj.opt_dateOpen = now;
      }
      obj.appId = userProfile.appId;
      obj.ts_created = now + a;
      obj.ts = now + a;
      obj.gameId = gameData.gameId;
      obj.icon = "/images/icons/momentum2.png";
      obj.id = obj.ts + '-' + gameCode + '-momentum';
      obj.status = 'active';
      obj.opt_visible = true;
      obj.opt_available = true;
      obj.type = 'momentum';
      obj.path = RandomString(18, 'abcdefghijklmnopqrstuvwxyz0123456789');
      objArr.push(obj)
    }
    return objArr;
  }

  function removeItem(item) {
    setConfirmMessage(`Are you sure you want to remove the <b>${item.name}</b> from the upload list? This action cannot be undone.`);
    approveRef.current = () => {
      let tempArr = [...arrToUpload]
      setArrToUpload([...tempArr.filter(i => i.id !== item.id)])
    }
  }

  function cancelUpload() {
    setArrToUpload([]);
    document.getElementById('fileUpload').value = null;
    // cancel();
  }

  function proceedWithUpload() {
    if (location.pathname.includes('/preview/manager/')) {
      setAlertMessage('Sorry! Data cannot be saved in Preview Mode.');
      return;
    }
    setConfirmMessage(`Are you sure you are ready to upload ${arrToUpload.length} Momentum Cards from the list? ${membership.membership < 1 ?
      `This will leave you with <b>${availableSpots - arrToUpload.length}</b> spots for other Game Elements.` :
      ''
      }`);
    async function uploadList() {
      setLoading(true);
      const res = await UseCloudFunction(
        'uploadElements',
        {
          'arr': arrToUpload,
          'appId': userProfile.appId,
          'gameId': gameData.gameId,
          'userName': `${userProfile.fName} ${userProfile.lName}`
        }
      )
      if (res.error) {
        setAlertMessage(`Something went wrong. (Error: ${res.error})`);
        setLoading(false);
        cancelUpload();
        trackException(`Momentum Cards Upload Error: ${res.error}`);
        return;
      }
      setAlertMessage('Momentum Cards Upload completed successfully! Go to the Momentum Cards List to view your new content.');
      setLoading(false);
      trackEvent(
        'momentum_uploaded',
        'Game Manager',
        `${arrToUpload.length} Momentum Cards were uploaded.`,
        null,
        userMembership
      );
      cancelUpload();
    }
    approveRef.current = () => {
      uploadList();
    }
  }

  return (
    <div className='momentum-upload-page'>
      <div className='g-card momentum-card'>
        <BackButton cancel={() => navigate(-1)} />
        <div className="g-space-1"></div>
        <div className='card-title'>
          <ImageContainer src='/images/icons/momentum2.png' alt='momentum icon' className='head-icon' />
          Upload a List of Momentum Cards
        </div>
        <hr />
        <p><b>Available Spots: {availableSpots}</b></p>
        <p>You can upload a list of Momentum Cards to make the content creation process easier.
          The only part of each card that cannot be created during this process is the icon.
          You will have to edit each card after it has been uploaded to give it a unique icon. A maximum of 200 elements can be uploaded at once.</p>
        <p>To upload a list of Momentum Cards, make a copy of <b><a href='https://docs.google.com/spreadsheets/d/1p9Im8OGVk1erZEP1N2deHEYMNeuKT9e5RxIX-WGXtGg/copy' target="_blank" rel="noreferrer">THIS GOOGLE SHEET</a></b> and export it as a CSV or download <b><a href="https://drive.google.com/uc?export=download&id=1vBCc8a3aAtZxATMejt3MYNuavXn0XQ7a" download>THIS CSV</a></b>. Then, upload the completed roster here.</p>
        <div className="g-space-1"></div>
        <div style={{ cursor: 'pointer' }}>
          <input type="file" id="fileUpload" accept=".csv" onChange={(e) => handleUpload(e.target.files[0])} />
          <button className="g-button" type="button" tabIndex="-1" onClick={() => clickUpload()}>Upload a List of Momentum Cards</button>
        </div>
        <div className="g-space-1"></div>
        {
          (arrToUpload.length > 0 && !loading) &&
          <div>
            <hr />
            <p><b>Momentum Cards to be Uploaded</b></p>
            {
              arrToUpload.length > 0 &&

              <div className='upload-count'>
                <div className='meta'>{arrToUpload.length} Momentum Cards</div>
              </div>
            }
            <div className='upload-list'>
              {
                arrToUpload.map(i => (
                  <div key={i.id}>
                    <UploadMomentumPreview
                      item={i}
                      removeItem={removeItem}
                      gameElements={gameElements}
                    />
                  </div>
                ))
              }
            </div>
            <div className='g-space-1'></div>
            {
              (loading)
                ?
                <div className='buttons'>
                  <button type='button' className='g-button'>Cancel</button>
                  <button type='button' className='g-button' ><ThreeDotsLoader /></button>
                </div>
                :
                <div className='buttons'>
                  <button type='button' className='g-button' onClick={() => cancelUpload()}>Cancel</button>
                  <button type='button' className='g-button primary' onClick={() => proceedWithUpload()}>Upload This List</button>
                </div>
            }
            <div className='g-space-1'></div>
          </div>
        }
      </div>
      <ModalAlert
        show={alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage}
        onApprove={accessDeniedRef.current}
      />
      <ModalConfirm
        show={confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current}
      />
      <FullPageLoader show={loading} />
    </div>
  )
}

export default MomentumUploadPage